// src/pages/personal/analytics/utils/chartDataProcessor.ts
import { TFunction } from 'i18next';
import { 
  Period, 
  MetricType, 
  ChartDataPoint 
} from '../types/types';

// Флаг для включения/отключения логирования (удобно для отладки)
const DEBUG_MODE = false;

// Дефолтные значения для случаев проблем с i18n
const DEFAULT_WEEKDAYS_SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const DEFAULT_WEEKDAYS = ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"];
const DEFAULT_MONTHS_SHORT = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];
const DEFAULT_MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];

/**
 * Логирование с учетом настроек отладки
 */
const debugLog = (...args: any[]) => {
  if (DEBUG_MODE) {
    console.log(...args);
  }
};

/**
 * Получение массива из результата i18n с защитой от ошибок
 */
const getSafeTranslationArray = (t: TFunction, key: string, defaultValues: string[]): string[] => {
  try {
    const translatedValues = t(key, { returnObjects: true });
    
    // Проверяем, является ли результат массивом
    if (Array.isArray(translatedValues) && translatedValues.length === defaultValues.length) {
      return translatedValues as string[];
    } 
    // Проверяем, является ли результат объектом с числовыми ключами
    else if (typeof translatedValues === 'object' && translatedValues !== null) {
      const valuesArray = Object.values(translatedValues);
      if (valuesArray.length === defaultValues.length) {
        // Явно преобразуем каждый элемент в строку
        return valuesArray.map(item => String(item));
      }
    }
    
    // Возвращаем значения по умолчанию, если что-то пошло не так
    return defaultValues;
  } catch (error) {
    console.warn(`Failed to get translated values for ${key}, using defaults`, error);
    return defaultValues;
  }
};

/**
 * Основная функция обработки данных для графиков
 * @param data - Исходные данные с API
 * @param period - Выбранный период (день, неделя, месяц и т.д.)
 * @param metric - Выбранная метрика (выручка, заказы и т.д.)
 * @param t - Функция перевода i18n
 * @param timezone - Часовой пояс из ответа API (если есть)
 * @returns Массив данных для отображения на графике
 */
export function processChartData(
  data: any[], 
  period: Period, 
  metric: MetricType,
  t: TFunction,
  timezone?: string
): ChartDataPoint[] {
  if (!data || data.length === 0) {
    return [];
  }
  
  debugLog(`Processing chart data for period: ${period}, metric: ${metric}, timezone: ${timezone}`);
  
  // Если данные пришли в формате chart_data, используем их напрямую
  if (data.length > 0 && data[0].hasOwnProperty(metric)) {
    // Проверка, если данные уже отформатированы для нужного периода
    if (period === 'year' && data[0].hasOwnProperty('name') && !data[0].hasOwnProperty('hour')) {
      debugLog('Using pre-formatted yearly data');
      return data.map(item => ({
        name: item.name || '',
        displayName: item.displayName || item.name || '',
        [metric]: item[metric] || 0,
        isSunday: item.isSunday || false
      }));
    }
    
    // Если это данные не в нужном формате для годового периода, обрабатываем их
    if (period === 'year') {
      debugLog('Processing yearly data from hour format');
      return generateYearData(data, metric, t);
    }
    
    return data.map(item => ({
      name: item.name || '',
      displayName: item.displayName || item.name || '',
      [metric]: item[metric] || 0,
      isSunday: item.isSunday || false
    }));
  }
  
  // Иначе обрабатываем данные в зависимости от периода
  try {
    switch (period) {
      case 'day':
        return generateDayData(data, metric);
      case 'week':
        return generateWeekData(data, metric, t);
      case 'month':
        return generateMonthData(data, metric);
      case 'quarter':
        return generateQuarterData(data, metric);
      case 'year':
        return generateYearData(data, metric, t);
      case 'custom':
        return generateCustomData(data, metric, t);
      default:
        return [];
    }
  } catch (error) {
    console.error('Error processing chart data:', error);
    return [];
  }
}

/**
 * Генерация данных для дневного графика (по часам)
 * @param data Исходные данные
 * @param metric Тип метрики
 * @returns Данные для графика
 */
export function generateDayData(data: any[], metric: MetricType): ChartDataPoint[] {
  // Группируем данные по часам и суммируем метрики
  const aggregatedByHour: Record<number, number> = {};
  
  data.forEach((item: any) => {
    const hour = item.hour;
    if (hour === undefined || hour === null) return;
    
    if (!aggregatedByHour[hour]) {
      aggregatedByHour[hour] = 0;
    }
    
    // Суммируем метрику для этого часа
    if (item[metric] !== undefined) {
      aggregatedByHour[hour] += Number(item[metric] || 0);
    }
  });
  
  // Генерация 24 часовых столбцов
  return Array.from({ length: 24 }, (_, hour) => {
    // Форматируем час в формате "ЧЧ:00"
    const formattedHour = `${hour.toString().padStart(2, '0')}:00`;
    
    // Проверяем, является ли этот час одной из ключевых точек
    const isKeyHour = hour % 4 === 0;
    
    return {
      name: formattedHour,
      displayName: formattedHour,
      [metric]: aggregatedByHour[hour] || 0,
      isKeyHour
    };
  });
}

/**
 * Генерация данных для недельного графика (по дням недели)
 * @param data Исходные данные
 * @param metric Тип метрики
 * @param t Функция перевода
 * @returns Данные для графика
 */
export function generateWeekData(data: any[], metric: MetricType, t: TFunction): ChartDataPoint[] {
  // Получаем безопасные массивы дней недели
  const weekdaysShort = getSafeTranslationArray(t, 'formats.weekdaysShort', DEFAULT_WEEKDAYS_SHORT);
  const weekdays = getSafeTranslationArray(t, 'formats.weekdays', DEFAULT_WEEKDAYS);
  
  // Группируем данные по дням недели и суммируем метрики
  const aggregatedByDayOfWeek: Record<number, number> = {};
  
  data.forEach((item: any) => {
    const dayOfWeek = item.day_of_week;
    if (!dayOfWeek || dayOfWeek < 1 || dayOfWeek > 7) return;
    
    if (!aggregatedByDayOfWeek[dayOfWeek]) {
      aggregatedByDayOfWeek[dayOfWeek] = 0;
    }
    
    // Суммируем метрику для этого дня недели
    if (item[metric] !== undefined) {
      aggregatedByDayOfWeek[dayOfWeek] += Number(item[metric] || 0);
    }
  });
  
  return weekdaysShort.map((day, index) => {
    // index начинается с 0, а day_of_week с 1, поэтому +1
    const dayOfWeek = index + 1;
    
    return {
      name: day,
      displayName: weekdays[index],
      [metric]: aggregatedByDayOfWeek[dayOfWeek] || 0
    };
  });
}

/**
 * Генерация данных для месячного графика (по дням месяца)
 * @param data Исходные данные
 * @param metric Тип метрики
 * @returns Данные для графика
 */
export function generateMonthData(data: any[], metric: MetricType): ChartDataPoint[] {
  // Определяем текущий месяц и год
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  // Определяем количество дней в текущем месяце
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // Группируем данные по дням месяца и суммируем метрики
  const aggregatedByDay: Record<number, number> = {};
  
  data.forEach((item: any) => {
    const day = item.day;
    if (!day || day < 1 || day > daysInMonth) return;
    
    if (!aggregatedByDay[day]) {
      aggregatedByDay[day] = 0;
    }
    
    // Суммируем метрику для этого дня
    if (item[metric] !== undefined) {
      aggregatedByDay[day] += Number(item[metric] || 0);
    }
  });
  
  return Array.from({ length: daysInMonth }, (_, day) => {
    // День месяца (1-31)
    const dayOfMonth = day + 1;
    
    // Определяем, является ли день воскресеньем
    const date = new Date(year, month, dayOfMonth);
    const isSunday = date.getDay() === 0;
    
    return {
      name: dayOfMonth.toString(),
      displayName: dayOfMonth.toString(),
      [metric]: aggregatedByDay[dayOfMonth] || 0,
      isSunday
    };
  });
}

/**
 * Генерация данных для квартального графика (по неделям)
 * @param data Исходные данные
 * @param metric Тип метрики
 * @returns Данные для графика
 */
export function generateQuarterData(data: any[], metric: MetricType): ChartDataPoint[] {
  // Определяем текущую дату и квартал
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  
  // Определяем начальную неделю квартала
  const firstDayOfQuarter = new Date(now.getFullYear(), currentQuarter * 3, 1);
  const firstWeek = getWeekNumber(firstDayOfQuarter);
  
  // Группируем данные по неделям и суммируем метрики
  const aggregatedByWeek: Record<number, number> = {};
  
  data.forEach((item: any) => {
    const week = item.week;
    if (!week) return;
    
    if (!aggregatedByWeek[week]) {
      aggregatedByWeek[week] = 0;
    }
    
    // Суммируем метрику для этой недели
    if (item[metric] !== undefined) {
      aggregatedByWeek[week] += Number(item[metric] || 0);
    }
  });
  
  // Генерируем 13 недель квартала
  return Array.from({ length: 13 }, (_, index) => {
    const weekNumber = firstWeek + index;
    
    return {
      name: weekNumber.toString(),
      displayName: weekNumber.toString(),
      [metric]: aggregatedByWeek[weekNumber] || 0
    };
  });
}

/**
 * Генерация данных для годового графика (по месяцам)
 * @param data Исходные данные
 * @param metric Тип метрики
 * @param t Функция перевода
 * @returns Данные для графика
 */
export function generateYearData(data: any[], metric: MetricType, t: TFunction): ChartDataPoint[] {
  // Получаем безопасные массивы месяцев
  const monthsShort = getSafeTranslationArray(t, 'formats.monthsShort', DEFAULT_MONTHS_SHORT);
  const months = getSafeTranslationArray(t, 'formats.months', DEFAULT_MONTHS);
  
  // Если в данных уже есть готовые данные по месяцам
  if (data.length > 0 && data[0].name && data[0].displayName && data[0].hasOwnProperty(metric)) {
    debugLog('Using pre-formatted monthly data');
    return data;
  }
  
  // Определяем текущий год
  const now = new Date();
  const currentYear = now.getFullYear();
  
  // Группируем данные по месяцам и суммируем метрики
  const aggregatedByMonth: Record<number, number> = {};
  
  data.forEach((item: any) => {
    const month = item.month;
    if (!month || month < 1 || month > 12) return;
    
    if (!aggregatedByMonth[month]) {
      aggregatedByMonth[month] = 0;
    }
    
    // Суммируем метрику для этого месяца
    if (item[metric] !== undefined) {
      aggregatedByMonth[month] += Number(item[metric] || 0);
    }
  });
  
  return monthsShort.map((month, index) => {
    // index начинается с 0, а month с 1, поэтому +1
    const monthNumber = index + 1;
    
    return {
      name: month,
      displayName: months[index],
      year: currentYear,
      month: monthNumber,
      [metric]: aggregatedByMonth[monthNumber] || 0
    };
  });
}

/**
 * Генерация данных для произвольного периода
 * @param data Исходные данные
 * @param metric Тип метрики
 * @param t Функция перевода
 * @returns Данные для графика
 */
export function generateCustomData(data: any[], metric: MetricType, t: TFunction): ChartDataPoint[] {
  // Если нет данных, возвращаем пустой массив
  if (!data || data.length === 0) return [];
  
  // Определяем диапазон дат в данных
  const dates = data
    .map(item => {
      if (item.date) {
        return new Date(item.date);
      } else if (item.year && item.month && item.day) {
        return new Date(item.year, item.month - 1, item.day);
      }
      return null;
    })
    .filter(Boolean) as Date[];
  
  if (dates.length === 0) return [];
  
  const startDate = new Date(Math.min(...dates.map(d => d.getTime())));
  const endDate = new Date(Math.max(...dates.map(d => d.getTime())));
  const daysDifference = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  
  // Выбираем группировку в зависимости от длины периода
  try {
    if (daysDifference <= 1) {
      // В течение 1 дня - по часам
      return generateDayData(data, metric);
    } else if (daysDifference <= 92) {
      // От 1 до 92 дней - по дням
      return generateMonthData(data, metric);
    } else if (daysDifference <= 365) {
      // От 92 до 365 дней - по месяцам
      return generateYearData(data, metric, t);
    } else {
      // Более 365 дней - по годам
      return generateMultiYearData(data, metric);
    }
  } catch (error) {
    console.error('Error generating custom data:', error);
    return [];
  }
}

/**
 * Генерация данных для нескольких лет
 * @param data Исходные данные
 * @param metric Тип метрики
 * @returns Данные для графика
 */
export function generateMultiYearData(data: any[], metric: MetricType): ChartDataPoint[] {
  const aggregatedByYear: Record<number, number> = {};
  
  data.forEach((item: any) => {
    const year = item.year || (item.date ? new Date(item.date).getFullYear() : null);
    if (!year) return;
    
    if (!aggregatedByYear[year]) {
      aggregatedByYear[year] = 0;
    }
    
    if (item[metric] !== undefined) {
      aggregatedByYear[year] += Number(item[metric] || 0);
    }
  });
  
  return Object.entries(aggregatedByYear)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([year, value]) => ({
      name: year,
      displayName: year,
      [metric]: value,
      year: parseInt(year)
    }));
}

/**
 * Получение номера недели для даты
 * @param date Дата
 * @returns Номер недели в году
 */
export function getWeekNumber(date: Date): number {
  const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
  const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
  return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
}